import { Filter, GetQueryParams } from '../types/api.type';

const ALLOWED_OPERATORS = new Set([
  'equals',
  'contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'greater_equal_than',
  'lesser_than',
  'lesser_equal_than',
  'includes'
]);
const MAX_FB_LENGTH = 4000;

/** Separates field|op|value within one filter. Encoded away inside segments via encodeURIComponent. */
const FILTER_PART_SEP = '|';
/** Separates filters in the fb blob. Encoded away inside segments via encodeURIComponent. */
const FILTER_SEP = ',';

const OPERATOR_TO_SHORT: Record<string, string> = {
  equals: 'eq',
  contains: 'contains',
  starts_with: 'sw',
  ends_with: 'ew',
  greater_than: 'gt',
  greater_equal_than: 'gte',
  lesser_than: 'lt',
  lesser_equal_than: 'lte',
  includes: 'includes'
};

const SHORT_TO_OPERATOR: Record<string, string> = {
  eq: 'equals',
  equals: 'equals',
  contains: 'contains',
  sw: 'starts_with',
  starts_with: 'starts_with',
  ew: 'ends_with',
  ends_with: 'ends_with',
  gt: 'greater_than',
  greater_than: 'greater_than',
  gte: 'greater_equal_than',
  greater_equal_than: 'greater_equal_than',
  lt: 'lesser_than',
  lesser_than: 'lesser_than',
  lte: 'lesser_equal_than',
  lesser_equal_than: 'lesser_equal_than',
  includes: 'includes'
};

const isValidFilter = (value: unknown): value is Filter => {
  if (!value || typeof value !== 'object') return false;
  const filter = value as Filter;
  if (typeof filter.field !== 'string' || !filter.field) return false;
  if (typeof filter.operator !== 'string' || !ALLOWED_OPERATORS.has(filter.operator)) return false;
  const filterValue = filter.value;
  return (
    typeof filterValue === 'string' ||
    typeof filterValue === 'number' ||
    typeof filterValue === 'boolean'
  );
};

const encodeFbSegment = (value: string): string => encodeURIComponent(value);

const decodeFbSegment = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** Compact fb: field|op|value,field|op|value (each segment percent-encoded). */
export const encodeFiltersToFb = (filterBy: Filter[]): string =>
  filterBy
    .map((f) => {
      const shortOp = OPERATOR_TO_SHORT[f.operator] ?? f.operator;
      return [f.field, shortOp, String(f.value)]
        .map((part) => encodeFbSegment(part))
        .join(FILTER_PART_SEP);
    })
    .join(FILTER_SEP);

const parseCompactFb = (fb: string): Filter[] => {
  if (!fb) return [];
  const filters: Filter[] = [];

  for (const rawFilter of fb.split(FILTER_SEP)) {
    if (!rawFilter) continue;
    const parts = rawFilter.split(FILTER_PART_SEP);
    if (parts.length !== 3) continue;

    const field = decodeFbSegment(parts[0]);
    const shortOp = decodeFbSegment(parts[1]);
    const value = decodeFbSegment(parts[2]);
    const operator = SHORT_TO_OPERATOR[shortOp];

    if (!field || value === '' || !operator || !ALLOWED_OPERATORS.has(operator)) continue;

    filters.push({ field, operator, value });
  }

  return filters;
};

const parseJsonFb = (fb: string): Filter[] => {
  try {
    const parsed = JSON.parse(fb);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidFilter);
  } catch {
    return [];
  }
};

const parseFbParam = (fb: string | null): Filter[] => {
  if (!fb || fb.length > MAX_FB_LENGTH) return [];
  const trimmed = fb.trim();
  // Legacy bookmarks: JSON array form
  if (trimmed.startsWith('[')) {
    return parseJsonFb(trimmed);
  }
  return parseCompactFb(trimmed);
};

export type ListFilterSearchOptions = {
  /** Used when `fb` is absent — preserves existing deep links (venue_id, starts_on, etc.). */
  parseExtra?: (params: URLSearchParams) => Filter[];
  /**
   * When true, keep scope in React/API state but do not write it to the URL.
   * Use for lists with a fixed scope and no scope tabs.
   */
  omitScopeFromUrl?: boolean;
};

export const parseListFiltersFromSearch = (
  search: string,
  defaults: GetQueryParams,
  options?: ListFilterSearchOptions
): GetQueryParams => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const scope = options?.omitScopeFromUrl
    ? defaults.scope
    : (params.get('scope') ?? defaults.scope);

  let filterBy: Filter[] | undefined;
  if (params.has('fb')) {
    filterBy = parseFbParam(params.get('fb'));
  } else {
    const extra = options?.parseExtra?.(params) ?? [];
    if (extra.length) filterBy = extra;
  }

  return {
    ...defaults,
    scope,
    data: {
      ...defaults.data,
      ...(filterBy ? { filterBy } : {})
    }
  };
};

export const listFiltersToSearchParams = (
  filters: GetQueryParams,
  options?: Pick<ListFilterSearchOptions, 'omitScopeFromUrl'>
): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.scope && !options?.omitScopeFromUrl) {
    params.set('scope', filters.scope);
  }

  const filterBy = filters.data?.filterBy?.filter(
    (f) => f != null && f.field && f.value !== '' && f.value != null
  );
  if (filterBy?.length) {
    const fb = encodeFiltersToFb(filterBy);
    if (fb.length <= MAX_FB_LENGTH) {
      params.set('fb', fb);
    }
  }

  return params;
};

export const searchParamsEqual = (a: URLSearchParams, b: URLSearchParams): boolean => {
  const aKeys = [...a.keys()].sort();
  const bKeys = [...b.keys()].sort();
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a.get(key) === b.get(key));
};

/** Read a filter value for FilterForm hydration. */
export const getFilterValue = (
  filterBy: Filter[] | undefined,
  field: string,
  operator?: string
): string => {
  const match = filterBy?.find(
    (f) => f.field === field && (operator === undefined || f.operator === operator)
  );
  return match?.value != null && match.value !== '' ? String(match.value) : '';
};

/**
 * Keep deep-link-only fields (not in FilterForm) when applying form filters.
 * e.g. registrations `course_id` / `order_id`, invoices `course_id`, refunds `order_id`.
 */
export const preserveFilterFields = (
  next: Filter[],
  previous: Filter[] | undefined,
  fields: string[]
): Filter[] => {
  const preserved = previous?.filter((f) => fields.includes(f.field)) ?? [];
  const without = next.filter((f) => !fields.includes(f.field));
  const merged = [...without];
  for (const p of preserved) {
    if (!merged.some((f) => f.field === p.field)) merged.push(p);
  }
  return merged;
};

/** Calendar / legacy deep-link helpers for classes list. */
export const parseClassesDeepLinkFilters = (params: URLSearchParams): Filter[] => {
  const filterBy: Filter[] = [];
  const startsOn = params.get('starts_on');
  const endsOn = params.get('ends_on');
  const locationId = params.get('location_id');

  if (startsOn) {
    filterBy.push({ operator: 'greater_equal_than', field: 'date', value: startsOn });
  }
  if (endsOn) {
    filterBy.push({ operator: 'lesser_equal_than', field: 'date', value: endsOn });
  }
  if (locationId && Number(locationId)) {
    filterBy.push({ operator: 'equals', field: 'location_id', value: locationId });
  }
  return filterBy;
};

/** Venue deep-link helper for sessions list. */
export const parseSessionsDeepLinkFilters = (params: URLSearchParams): Filter[] => {
  const venueId = params.get('venue_id');
  if (!venueId) return [];
  return [{ field: 'venue_id', operator: 'equals', value: venueId }];
};

/** Organization deep-link helper for venues list. */
export const parseVenuesDeepLinkFilters = (params: URLSearchParams): Filter[] => {
  const organizationId = params.get('organization_id');
  if (!organizationId) return [];
  return [{ field: 'organization_id', operator: 'equals', value: organizationId }];
};

/** Orders / Payments / Refunds weekly-report style deep links. */
export const parseCreatedAtLocationDeepLinkFilters = (params: URLSearchParams): Filter[] => {
  const filterBy: Filter[] = [];
  const locationId = params.get('location_id');
  const createdAtGte = params.get('created_at_gteq_datetime');
  const createdAtLte = params.get('created_at_lteq_datetime');

  if (locationId) {
    filterBy.push({ field: 'location_id', operator: 'equals', value: locationId });
  }
  if (createdAtGte) {
    filterBy.push({
      field: 'created_at',
      operator: 'greater_equal_than',
      value: createdAtGte
    });
  }
  if (createdAtLte) {
    filterBy.push({
      field: 'created_at',
      operator: 'lesser_equal_than',
      value: createdAtLte
    });
  }
  return filterBy;
};

/** Refunds: order_id + optional location/created_at deep links. */
export const parseRefundsDeepLinkFilters = (params: URLSearchParams): Filter[] => {
  const filterBy = parseCreatedAtLocationDeepLinkFilters(params);
  const orderId = params.get('order_id');
  if (orderId) {
    filterBy.push({ field: 'order_id', operator: 'equals', value: orderId });
  }
  return filterBy;
};

/** Invoices: legacy Rails-style course filter param. */
export const parseInvoicesDeepLinkFilters = (params: URLSearchParams): Filter[] => {
  const courseId = params.get('course_invoices_course_id_eq');
  if (!courseId) return [];
  return [{ field: 'course_id', operator: 'equals', value: courseId }];
};

/** Registrations: course_id or order_id deep links. */
export const parseRegistrationsDeepLinkFilters = (params: URLSearchParams): Filter[] => {
  const orderId = params.get('order_id');
  if (orderId) {
    return [{ field: 'order_id', operator: 'equals', value: orderId }];
  }
  const courseId = params.get('course_id');
  if (courseId) {
    return [{ field: 'course_id', operator: 'equals', value: courseId }];
  }
  return [];
};

/** Discounts: user_id deep link. */
export const parseDiscountsDeepLinkFilters = (params: URLSearchParams): Filter[] => {
  const userId = params.get('user_id');
  if (!userId) return [];
  return [{ field: 'user_id', operator: 'equals', value: userId }];
};
